import { useQuery } from "@tanstack/react-query";
import { isAddress, type Address } from "viem";
import { useAccount } from "wagmi";
import { fetchUserPnlHistory, fetchUserTvlHistory } from "@/services";
import { QUERY_CONFIG, QUERY_KEYS } from "@/config";
import type { UserHistoryData, UserHistoryType } from "@/types/api.types";

interface UseUserHistoryOptions {
    type: UserHistoryType;
    daysAgo: number;
    address?: Address;
    enabled?: boolean;
}

export const useUserHistory = ({ type, daysAgo, address, enabled = true }: UseUserHistoryOptions) => {
    const { address: accountAddress } = useAccount();
    const resolvedAddress = address ?? accountAddress;

    return useQuery<UserHistoryData>({
        queryKey: QUERY_KEYS.PROTOCOL.USER_HISTORY(type, resolvedAddress, daysAgo),
        queryFn: () =>
            type === "tvl"
                ? fetchUserTvlHistory(resolvedAddress!, daysAgo)
                : fetchUserPnlHistory(resolvedAddress!, daysAgo),
        enabled: enabled && !!resolvedAddress && isAddress(resolvedAddress),
        ...QUERY_CONFIG.PROTOCOL,
    });
};

export const useUserTvlHistory = (daysAgo: number, address?: Address, enabled = true) =>
    useUserHistory({ type: "tvl", daysAgo, address, enabled });

export const useUserPnlHistory = (daysAgo: number, address?: Address, enabled = true) =>
    useUserHistory({ type: "pnl", daysAgo, address, enabled });
